import React, { useEffect, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Badge, 
  Box, 
  useTheme,
  useMediaQuery,
  Container,
  Drawer,
  TextField,
  InputAdornment,
} from '@mui/material';

import { 
  ShoppingCart, 
  Menu as MenuIcon,
  Home as HomeIcon,
  ShoppingBag as ProductsIcon,
  FavoriteBorder as FavoriteBorderIcon,
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminIcon,
  ContactMail as ContactIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Description as DescriptionIcon,
  Instagram,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import snapchatLogo from '../pages/snapchat-logo.png';
import tiktokLogo from '../pages/tik tok-logo.png';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'bratanfamily59@gmail.com';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { count } = useCart();
  const { ids } = useWishlist();
  const wishlistCount = ids.length;
  const { user, hasNewOrderNotification, setHasNewOrderNotification } = useAuth();

  const isAdminUser =
    !!user &&
    typeof user.email === 'string' &&
    user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/account' && hasNewOrderNotification) {
      setHasNewOrderNotification(false);
    }
  }, [location.pathname, hasNewOrderNotification, setHasNewOrderNotification]);

  const navItems = [
    { text: 'Accueil', path: '/', icon: <HomeIcon sx={{ mr: 1 }} /> },
    { text: 'Produits', path: '/products', icon: <ProductsIcon sx={{ mr: 1 }} /> },
    { text: 'Contact', path: '/contact', icon: <ContactIcon sx={{ mr: 1 }} /> },
  ];

  if (isAdminUser) {
    navItems.push({ text: 'Admin', path: '/admin', icon: <AdminIcon sx={{ mr: 1 }} /> });
  }

  const legalItems = [
    {
      text: 'Mentions légales',
      path: '/legal-notice',
      icon: <DescriptionIcon sx={{ mr: 1 }} />,
    },
    {
      text: 'Politique de confidentialité',
      path: '/privacy-policy',
      icon: <PolicyIcon sx={{ mr: 1 }} />,
    },
    {
      text: 'Conditions générales',
      path: '/terms-conditions',
      icon: <GavelIcon sx={{ mr: 1 }} />,
    },
  ];

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {isMobile ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <IconButton
                  size="large"
                  aria-label="menu"
                  color="inherit"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'primary.main',
                  }}
                >
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="Bratan Family"
                    sx={{
                      height: 32,
                      mr: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Bratan Family
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                }}
              >
                <IconButton
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => {
                    setMobileSearchOpen((prev) => !prev);
                  }}
                >
                  <SearchIcon />
                </IconButton>

                <IconButton
                  component={Link}
                  to="/wishlist"
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Badge badgeContent={wishlistCount} color="primary">
                    <FavoriteBorderIcon />
                  </Badge>
                </IconButton>

                <IconButton 
                  component={Link}
                  to="/account"
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => {
                    if (hasNewOrderNotification) {
                      setHasNewOrderNotification(false);
                    }
                  }}
                >
                  <Badge
                    color="primary"
                    badgeContent={1}
                    overlap="circular"
                    invisible={!user || !hasNewOrderNotification}
                  >
                    <AccountCircleIcon />
                  </Badge>
                </IconButton>

                <IconButton 
                  component={Link} 
                  to="/cart" 
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Badge badgeContent={count} color="primary">
                    <ShoppingCart />
                  </Badge>
                </IconButton>
              </Box>

              <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
              >
                <Box
                  sx={{
                    width: 260,
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      component={Link}
                      to="/"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Box
                        component="img"
                        src="/logo.png"
                        alt="Bratan Family"
                        sx={{ height: 48 }}
                      />
                    </Box>
                  </Box>

                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      startIcon={item.icon}
                      fullWidth
                      variant="outlined"
                      sx={{
                        justifyContent: 'flex-start',
                        mb: 1,
                        borderRadius: 2,
                        color:
                          location.pathname === item.path
                            ? 'primary.main'
                            : 'text.primary',
                        borderColor:
                          location.pathname === item.path
                            ? 'primary.main'
                            : 'divider',
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.text}
                    </Button>
                  ))}

                  <Box sx={{ flexGrow: 1 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <IconButton
                      color="inherit"
                      size="small"
                      aria-label="TikTok"
                      href="https://www.tiktok.com/@bratan.family"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box
                        component="img"
                        src={tiktokLogo}
                        alt="TikTok"
                        sx={{ width: 24, height: 24, borderRadius: '50%' }}
                      />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      size="small"
                      aria-label="Instagram"
                      href="https://www.instagram.com/bratan.family/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Instagram fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      size="small"
                      aria-label="Snapchat"
                      href="https://www.snapchat.com/add/bratanfamily45"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box
                        component="img"
                        src={snapchatLogo}
                        alt="Snapchat"
                        sx={{ width: 24, height: 24, borderRadius: '50%' }}
                      />
                    </IconButton>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    {legalItems.map((item) => (
                      <Button
                        key={item.path}
                        component={Link}
                        to={item.path}
                        startIcon={item.icon}
                        fullWidth
                        variant="outlined"
                        sx={{
                          justifyContent: 'flex-start',
                          mb: 1,
                          borderRadius: 2,
                          color:
                            location.pathname === item.path
                              ? 'primary.main'
                              : 'text.primary',
                          borderColor:
                            location.pathname === item.path
                              ? 'primary.main'
                              : 'divider',
                        }}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.text}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Drawer>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'primary.main',
                    mr: 4,
                  }}
                >
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="Bratan Family"
                    sx={{
                      height: 36,
                      mr: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Bratan Family
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color:
                        location.pathname === item.path
                          ? 'primary.main'
                          : 'text.primary',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      mx: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  flexShrink: 0,
                }}
              >
                <IconButton
                  component={Link}
                  to="/wishlist"
                  color="inherit"
                  sx={{
                    ml: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Badge badgeContent={wishlistCount} color="primary">
                    <FavoriteBorderIcon />
                  </Badge>
                </IconButton>

                <IconButton 
                  component={Link}
                  to="/account"
                  color="inherit"
                  sx={{
                    ml: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => {
                    if (hasNewOrderNotification) {
                      setHasNewOrderNotification(false);
                    }
                  }}
                >
                  <Badge
                    color="primary"
                    badgeContent={1}
                    overlap="circular"
                    invisible={!user || !hasNewOrderNotification}
                  >
                    <AccountCircleIcon />
                  </Badge>
                </IconButton>

                <IconButton 
                  component={Link} 
                  to="/cart" 
                  color="inherit"
                  sx={{
                    ml: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Badge badgeContent={count} color="primary">
                    <ShoppingCart />
                  </Badge>
                </IconButton>
              </Box>
            </>
          )}
        </Toolbar>

        {isMobile && mobileSearchOpen && (
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Rechercher un produit ou une catégorie..."
              value={mobileSearchValue}
              onChange={(e) => setMobileSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const q = mobileSearchValue.trim();
                  navigate(q ? `/products?q=${encodeURIComponent(q)}` : '/products');
                  setMobileSearchOpen(false);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </Container>
    </AppBar>
  );
};

export default Header;